import { useQuery } from '@tanstack/react-query';
import {
  ErrorCode,
  type ApiErrorBody,
  type ReferrerIntakeResult,
  type ReferrerIntakeSubmitBody,
  type ReferrerIntakeView,
} from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE } from '../../../lib/api';

/**
 * The public referrer intake `/t/:token` (docs/dev/api-referrer-partners.md „Public intake\", D-248): no session, the
 * partner's token is the only credential. Every failure arrives as an `ApiError` (404 invalid/inactive, 410 expired,
 * 400 with `details.fields`, 429 rate limit).
 */

const intakeBase = (token: string) => `/api/public/referrer-intake/${encodeURIComponent(token)}`;

async function intakeRequest<T>(
  token: string,
  path: string,
  init: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: string | null = null;
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  const response = await fetch(`${intakeBase(token)}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body,
    credentials: 'omit',
    signal: init.signal ?? null,
  });
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    const envelope = (json as Partial<ApiErrorBody> | null)?.error ?? {
      code: response.status === 429 ? ErrorCode.RATE_LIMITED : ErrorCode.INTERNAL,
      message: GENERIC_ERROR_MESSAGE,
    };
    throw new ApiError(response.status, envelope);
  }
  return json as T;
}

export const referrerIntakeKey = (token: string) => ['public-referrer-intake', token] as const;

export function useReferrerIntake(token: string) {
  return useQuery({
    queryKey: referrerIntakeKey(token),
    queryFn: ({ signal }) => intakeRequest<ReferrerIntakeView>(token, '', { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 5 * 60_000,
  });
}

export function submitReferrerIntake(
  token: string,
  body: ReferrerIntakeSubmitBody,
): Promise<ReferrerIntakeResult> {
  return intakeRequest<ReferrerIntakeResult>(token, '/submit', { method: 'POST', body });
}
